import type { EmojiId } from '@atlaskit/emoji/types';
import type { EmojiLoadSuccessCallback, EmojiLoadFailCallback } from '@atlaskit/emoji/emoji-utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	ConfigOptions,
	EmojiSSRConfig,
} from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';

export type CreateEmojiProviderParametersType = {
	cloudId: string;
	userId?: string;
	disableUpload?: boolean;
	emojiSSRConfig?: EmojiSSRConfig;
	emojiLoadCallbacks?: {
		onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
		onEmojiLoadFail?: EmojiLoadFailCallback;
	};
};

type ConfigOptionsWithGuest = ConfigOptions & {
	isLicensed?: boolean;
	isLoggedIn?: boolean;
};

export const BASE_URL = '/gateway/api/emoji';

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const isServerEnvironment = () => !!process.env.REACT_SSR; // "1" true || "" false || undefined false
const isClientEnvironment = () => !!window.__SSR_RENDERED__;

type SSRConditionalParams = {
	isSSR?: boolean;
	hasCloudId?: boolean;
	isGuestUser?: boolean;
};

export const ELEMENTS_BASE_URL = '/gateway/api/elements/emoji';

export const getOptimisticImageURL = (
	cloudId: string,
	emojiId: string,
	scale?: 'MDPI' | 'XXXHDPI',
) => {
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const baseUrl = fg('use-elements-stargate-emoji-path') ? ELEMENTS_BASE_URL : BASE_URL;
	return `${baseUrl}/${cloudId}/${emojiId}/path?scale=${scale ?? 'MDPI'}`;
};

export const getEmojiSSRSupport = (params: SSRConditionalParams): boolean => {
	const { isSSR, hasCloudId, isGuestUser } = params;

	const ssrEnabled = !!(isSSR && hasCloudId);

	// SSR Not Supported
	if (!ssrEnabled || isGuestUser) {
		return false;
	}

	// SSR For Logged in Users
	return true;
};

export const getEmojiLoadCallbacks = ({
	onEmojiLoadSuccess,
	onEmojiLoadFail,
}: {
	onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	onEmojiLoadFail?: EmojiLoadFailCallback;
}):
	| {
			onEmojiLoadSuccess: EmojiLoadSuccessCallback | undefined;
			onEmojiLoadFail: EmojiLoadFailCallback | undefined;
	  }
	| undefined => {
	if (!onEmojiLoadSuccess && !onEmojiLoadFail) {
		return undefined;
	}

	return {
		onEmojiLoadSuccess,
		onEmojiLoadFail,
	};
};

export const generateEmojiIdentifierFromEmojiId = (emojiId: EmojiId): string => {
	const { id, shortName } = emojiId;
	const regexp = new RegExp(/:([^:]+):/g); // tests and takes the value between two colons

	if (id) return id;

	if (regexp.test(shortName)) {
		return shortName.replaceAll(':', '');
	}

	return shortName;
};

export const createEmojiProviderParameters = (
	params: ConfigOptionsWithGuest,
): CreateEmojiProviderParametersType => {
	// Feature enabled flags
	const isSSR = isServerEnvironment() || isClientEnvironment();
	const isGuestUser = !params.isLicensed || !params.isLoggedIn;

	// Transform parameters
	const cloudId = params?.cloudId ?? '';
	const userId = params?.userId ?? undefined;
	const disableUpload = params?.disableUpload;
	const emojiSSRConfig = params?.emojiSSRConfig ?? {};

	// Enable emoji ssr
	const useEmojiSSR = getEmojiSSRSupport({
		isSSR,
		hasCloudId: !!cloudId,
		isGuestUser,
	});

	// Default emoji SSR interface
	const emojiSSRConfigOverride: EmojiSSRConfig | undefined = useEmojiSSR
		? {
				optimisticImageApi: {
					getUrl: (emojiId) =>
						getOptimisticImageURL(cloudId, generateEmojiIdentifierFromEmojiId(emojiId)),
				},
				...emojiSSRConfig,
			}
		: undefined;

	// Enable emoji load callbacks
	const emojiLoadCallbacks = getEmojiLoadCallbacks({
		onEmojiLoadSuccess: params?.onEmojiLoadSuccess,
		onEmojiLoadFail: params?.onEmojiLoadFail,
	});

	return {
		cloudId,
		userId,
		disableUpload,
		...(useEmojiSSR && { emojiSSRConfig: emojiSSRConfigOverride }),
		emojiLoadCallbacks,
	};
};
